import { useEffect, useRef } from "react";

const DEFAULT_ROOT_MARGIN = "0px 0px 320px 0px";

export default function useInfiniteScrollObserver({
  enabled = true,
  hasMore = false,
  isLoading = false,
  onLoadMore,
  rootRef = null,
  rootMargin = DEFAULT_ROOT_MARGIN,
  threshold = 0,
} = {}) {
  const sentinelRef = useRef(null);
  const onLoadMoreRef = useRef(onLoadMore);
  const isLoadingRef = useRef(isLoading);
  const hasMoreRef = useRef(hasMore);
  const enabledRef = useRef(enabled);
  const pendingTriggerRef = useRef(false);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
    if (!isLoading) {
      pendingTriggerRef.current = false;
    }
  }, [isLoading]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return;
        }
        if (!enabledRef.current || !hasMoreRef.current || isLoadingRef.current || pendingTriggerRef.current) {
          return;
        }

        pendingTriggerRef.current = true;
        onLoadMoreRef.current?.();
      },
      {
        root: rootRef?.current ?? null,
        rootMargin,
        threshold,
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, hasMore, rootMargin, rootRef, threshold]);

  return sentinelRef;
}
